import { ReactNode, useMemo, useState } from 'react';
import { useEffectOnce, useObservable } from '@paradigm/utils/src/hooks';

import UnifiedTradesService from '#/unified-rfqs/services/unified-trades';
import { Trade } from '#/unified-rfqs/entities/shared';
import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';
import { useDrfqService } from '#/unified-rfqs/contexts/drfqs';
import TradesStore from '#/unified-rfqs/store/trades';
import createSafeContext from '@paradigm/utils/createSafeContext';

const [useUnifiedTradesService, UnifiedTradesServiceProviderCtx] =
  createSafeContext<UnifiedTradesService>('UnifiedTradesService');

interface Props {
  readonly children: ReactNode;
  readonly tradesStore: TradesStore;
}

export { useUnifiedTradesService };
export function UnifiedTradesServiceProvider({ children, tradesStore }: Props) {
  const grfqService = useGrfqService();
  const drfqService = useDrfqService();
  const tradeHistoryService = useMemo(
    () =>
      new UnifiedTradesService({
        grfqService,
        drfqService,
        tradesStore,
      }),
    [grfqService, drfqService, tradesStore],
  );

  return (
    <UnifiedTradesServiceProviderCtx
      key={tradeHistoryService.id}
      value={tradeHistoryService}
    >
      {children}
    </UnifiedTradesServiceProviderCtx>
  );
}

export function useUnifiedTradesFetchData() {
  const myTradesService = useUnifiedTradesService();
  return useObservable(myTradesService.fetcher.fetchData);
}

export function useUnifiedTradesIdsList() {
  const myTradesService = useUnifiedTradesService();
  const [tradesIds, setTradesIds] = useState(
    myTradesService.fetcher.selectPaginatedIds(),
  );

  useEffectOnce(() => {
    return myTradesService.fetcher.subscribePaginatedIds(setTradesIds);
  });

  return tradesIds;
}

export function useMaybeTrade(tradeId: Trade['id']) {
  const myTradesService = useUnifiedTradesService();
  const ob = useMemo(
    () => myTradesService.item(tradeId),
    [myTradesService, tradeId],
  );
  return useObservable(ob);
}

/**
 * ATTENTION: This hook should be used only in places in which
 * the trade is guaranteed to exist.
 *
 */
export function useTrade(tradeId: Trade['id']) {
  const trade = useMaybeTrade(tradeId);

  if (trade == null) {
    throw new TypeError(`Trade ${tradeId} is null`);
  }

  return trade;
}
